import {Box, Stack, Typography, Tooltip} from "@mui/material";
import {subProjectLabel} from "../../../utils/subProjects";

export const ProjectStatus = {
  BUILDING_SCENARIOS: "BUILDING_SCENARIOS",
  IN_PROGRESS: "IN_PROGRESS",
  TESTING: "TESTING",
  PRODUCTION: "PRODUCTION"
}

const stages = [
  ProjectStatus.BUILDING_SCENARIOS,
  ProjectStatus.IN_PROGRESS,
  ProjectStatus.TESTING,
  ProjectStatus.PRODUCTION
];

export const stageLabels = {
  [ProjectStatus.BUILDING_SCENARIOS]: "Building Scenario",
  [ProjectStatus.IN_PROGRESS]: "In Progress",
  [ProjectStatus.TESTING]: "Testing",
  [ProjectStatus.PRODUCTION]: "Production"
};

// GitHub-like shades: no language reached the stage, some of them, most of them, every language
const EMPTY = "#E4E4EE";
const SHADES = ["#9BE9A8", "#40C463", "#1A7F37"];

const shadeOf = (reached, total) => {
  if (!total || !reached) return EMPTY;
  if (reached >= total) return SHADES[2];
  return reached / total <= 0.5 ? SHADES[0] : SHADES[1];
};

const stageIndex = (status) => Math.max(0, stages.indexOf(status || ProjectStatus.BUILDING_SCENARIOS));

const languageName = (subProject) => subProjectLabel(subProject) || "Sub project";

// Small row of the four stages for one sub project (tooltip list)
const LanguageStages = ({index}) => (
  <Stack direction={"row"} gap={0.375}>
    {stages.map((stage, i) => (
      <Box key={stage} sx={{width: 10, height: 4, borderRadius: 1, bgcolor: i <= index ? SHADES[2] : "rgba(255,255,255,0.25)"}}/>
    ))}
  </Stack>
);

// Stage progress of a project over its sub projects. A language has resolved every stage up to and including its
// own status (PRODUCTION = all four), and each stage is shaded by how many languages resolved it.
// Hovering a stage shows its count, hovering the label lists every language with its stage.
export const ProjectStageProgress = ({subProjects = []}) => {
  const languages = subProjects.length ? subProjects : [{id: "none", status: ProjectStatus.BUILDING_SCENARIOS}];
  const total = languages.length;
  const indexes = languages.map(subProject => stageIndex(subProject?.status));
  const reachedCounts = stages.map((stage, i) => indexes.filter(index => index >= i).length);

  // the stage every language has reached; "x / n" more are further along
  const commonIndex = Math.min(...indexes);
  const ahead = indexes.filter(index => index > commonIndex).length;

  const languagesTooltip = subProjects.length ? (
    <Stack gap={0.75} sx={{py: 0.25}}>
      {subProjects.map(subProject => {
        const index = stageIndex(subProject?.status);
        return (
          <Stack key={subProject.id} direction={"row"} alignItems={"center"} justifyContent={"space-between"} gap={2}>
            <Typography sx={{fontSize: 12, fontWeight: 600}}>{languageName(subProject)}</Typography>
            <Stack direction={"row"} alignItems={"center"} gap={1}>
              <Typography sx={{fontSize: 11, opacity: 0.85}}>{stageLabels[stages[index]]}</Typography>
              <LanguageStages index={index}/>
            </Stack>
          </Stack>
        );
      })}
    </Stack>
  ) : "";

  return (
    <Box>
      <Tooltip title={languagesTooltip} placement={"top"}>
        <Box sx={{display: "inline-block", cursor: subProjects.length ? "default" : undefined}}>
          <Typography sx={{fontSize: 14, fontWeight: 700, color: "#23264D", lineHeight: 1.3}}>
            {stageLabels[stages[commonIndex]]}
          </Typography>
          <Typography sx={{fontSize: 11, color: "#8A8AA0", mb: 0.75, minHeight: 15}}>
            {total > 1
              ? ahead ? `${ahead} of ${total} projects further` : `All ${total} projects`
              : ""}
          </Typography>
        </Box>
      </Tooltip>

      <Stack direction="row" gap={0.75}>
        {stages.map((stage, i) => (
          <Tooltip key={stage} title={`${stageLabels[stage]} · ${reachedCounts[i]} of ${total} ${total === 1 ? "sub project" : "sub projects"}`}>
            <Box sx={{
              width: 22,
              height: 6,
              borderRadius: 2,
              backgroundColor: shadeOf(reachedCounts[i], total),
              transition: "background-color 0.3s ease"
            }}/>
          </Tooltip>
        ))}
      </Stack>
    </Box>
  );
};
